import styled from 'styled-components'
import { type Ref } from 'react'
import { CarouselButton } from '@/features/shared/elements/Buttons'
import { hide as hideAnimation } from '@/features/shared/styles/effects'
import { type SurfaceOption, screenSizes } from '@shipt/design-system-themes'
import { rgba } from 'polished'
import { ChevronLeftIcon, ChevronRightIcon } from '@shipt/design-system-icons'

type Direction = 'left' | 'right'

type Props = {
  onClick?: () => void
  direction?: Direction
  surface?: SurfaceOption
  navButtonOverlayColor?: string
  ref: Ref<HTMLDivElement>
}

export const NavButton = ({
  onClick,
  direction = 'left',
  surface = 'default',
  navButtonOverlayColor,
  ref,
}: Props) => {
  const isLeft = direction === 'left'
  const ariaLabel = isLeft ? 'Previous items' : 'Next items'

  return (
    // data-hidden="true" in the markup so SSR + first client paint won't
    // show flash of nav buttons before onSwiper fires. visibility is flipped
    // imperatively afterwards.
    <NavSide ref={ref} data-hidden="true" className="hide-keyboarduser">
      <OverlayLayer
        $direction={direction}
        data-direction={direction}
        $surface={surface}
        $navButtonOverlayColor={navButtonOverlayColor}
      />
      <ButtonWrapper
        data-direction={direction}
        // Don't remove this className, it's used for Testimonials
        className="NavButton-wrapper"
      >
        <CarouselButton
          type="button"
          variant="ghost"
          // Size matches the astro inline button size
          size="md"
          icon={isLeft ? ChevronLeftIcon : ChevronRightIcon}
          onClick={onClick}
          aria-label={ariaLabel}
          // Do not remove this className, it is used within SubstitutionCard
          className={`NavButton-${direction}`}
        />
      </ButtonWrapper>
    </NavSide>
  )
}

const NavSide = styled.div`
  display: none;

  &[data-hidden='true'] {
    ${hideAnimation};
  }

  @media ${screenSizes.tablet} {
    &[data-hidden='false'] {
      display: block;
    }
  }
`

const OverlayLayer = styled.div<{
  $navButtonOverlayColor?: string
  $surface: SurfaceOption
  $direction: Direction
  'data-direction': Direction
}>`
  position: absolute;
  z-index: 2;
  top: 0;
  width: 5rem;
  height: 100%;
  pointer-events: none;

  &[data-direction='left'] {
    left: -0.5rem;
  }

  &[data-direction='right'] {
    right: -0.5rem;
  }

  background: ${({ $direction, theme, $surface, $navButtonOverlayColor }) => {
    let color = theme.color.background.base.brand.primary
    if ($surface === 'default') color = theme.white
    if ($navButtonOverlayColor === 'transparent') return null
    if ($navButtonOverlayColor) color = $navButtonOverlayColor
    return `linear-gradient(to ${$direction}, ${rgba(color, 0)} 0%, ${rgba(
      color,
      0.62,
    )} 39.46%, ${color})`
  }};
`

const ButtonWrapper = styled.div<{ 'data-direction': Direction }>`
  position: absolute;
  z-index: 2;
  top: 50%;
  border-radius: 100%;

  &[data-direction='left'] {
    left: 0;
    transform: translate(-25%, -50%);
  }

  &[data-direction='right'] {
    right: 0;
    transform: translate(25%, -50%);
  }
`
